import { ChevronLeft, ChevronRight } from "lucide-react";
import { useState } from "react";

// Presentation pieces shared by the two sections of the import page
// (établissements + fermetures) so both tables paginate and read identically.

export const COL_BASE =
    "px-3 py-2 text-xs text-gray-700 border-b border-gray-100 whitespace-nowrap";
export const TH =
    "px-3 py-2 text-[11px] font-semibold text-gray-500 text-left border-b border-gray-200 bg-gray-50 whitespace-nowrap";
export const PAGE_SIZE = 50;

export type StatColor = "green" | "blue" | "gray" | "red" | "violet" | "amber";

export function StatCard({
    label,
    value,
    sub,
    color,
}: {
    label: string;
    value: number;
    sub?: string;
    color?: StatColor;
}) {
    const colorMap: Record<StatColor, string> = {
        green: "text-green-700",
        blue: "text-blue-700",
        gray: "text-gray-700",
        red: "text-red-600",
        violet: "text-violet-700",
        amber: "text-amber-700",
    };
    const dotMap: Record<StatColor, string> = {
        green: "bg-green-500",
        blue: "bg-blue-500",
        gray: "bg-gray-400",
        red: "bg-red-500",
        violet: "bg-violet-500",
        amber: "bg-amber-500",
    };
    const c = color ?? "gray";
    return (
        <div className="rounded-xs border border-gray-100 bg-white p-4">
            <div className="flex items-center gap-1.5 mb-1">
                <span className={`inline-block w-2 h-2 rounded-full ${dotMap[c]}`} />
                <p className="text-[11px] text-gray-500">{label}</p>
            </div>
            <p className={`text-2xl font-bold tabular-nums ${colorMap[c]}`}>
                {value.toLocaleString("fr-FR")}
            </p>
            {sub && <p className="text-[11px] text-gray-400 mt-0.5">{sub}</p>}
        </div>
    );
}

export interface LogColumn<T> {
    header: string;
    render: (log: T) => React.ReactNode;
}

/**
 * Paginated log table. `total` is the server-side count, which can exceed
 * `logs.length` because the backend caps what it streams back per category.
 */
export function LogTable<T extends { rowIndex: number; siret: string }>({
    logs,
    total,
    columns,
}: {
    logs: T[];
    total: number;
    columns: LogColumn<T>[];
}) {
    const [page, setPage] = useState(0);

    if (logs.length === 0)
        return <p className="text-xs text-gray-400 py-4 text-center">Aucune entrée</p>;

    const totalPages = Math.ceil(logs.length / PAGE_SIZE);
    const safePage = Math.min(page, totalPages - 1);
    const slice = logs.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE);
    const from = safePage * PAGE_SIZE + 1;
    const to = Math.min((safePage + 1) * PAGE_SIZE, logs.length);

    return (
        <div>
            <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                    <thead>
                        <tr>
                            <th className={TH}>#</th>
                            {columns.map((c) => (
                                <th key={c.header} className={TH}>
                                    {c.header}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {slice.map((log) => (
                            <tr key={`${log.rowIndex}-${log.siret}`} className="hover:bg-gray-50">
                                <td className={`${COL_BASE} text-gray-400`}>{log.rowIndex}</td>
                                {columns.map((c) => (
                                    <td key={c.header} className={COL_BASE}>
                                        {c.render(log)}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="mt-2 flex items-center justify-between gap-4 px-1">
                <p className="text-[11px] text-gray-400 shrink-0">
                    {from}–{to} sur {logs.length.toLocaleString("fr-FR")} aperçus
                    {total > logs.length && (
                        <span className="text-amber-500 ml-1">
                            ({(total - logs.length).toLocaleString("fr-FR")} de plus non affichés)
                        </span>
                    )}
                </p>
                {totalPages > 1 && (
                    <div className="flex items-center gap-1 shrink-0">
                        <button
                            onClick={() => setPage((p) => Math.max(0, p - 1))}
                            disabled={safePage === 0}
                            className="flex items-center justify-center h-6 w-6 rounded border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                            <ChevronLeft className="h-3.5 w-3.5" />
                        </button>
                        <span className="text-[11px] text-gray-500 tabular-nums px-1">
                            {safePage + 1} / {totalPages}
                        </span>
                        <button
                            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                            disabled={safePage === totalPages - 1}
                            className="flex items-center justify-center h-6 w-6 rounded border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                            <ChevronRight className="h-3.5 w-3.5" />
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

/** Shared drag-and-drop CSV picker. */
export function CsvDropZone({
    file,
    onPick,
    disabled,
    hint,
}: {
    file: File | null;
    onPick: (f: File | null) => void;
    disabled?: boolean;
    hint?: string;
}) {
    const [isDragging, setIsDragging] = useState(false);

    const label = file
        ? `${file.name} (${(file.size / (1024 * 1024)).toFixed(1)} Mo)`
        : "Aucun fichier sélectionné";

    return (
        <div
            className={`rounded-xs border-2 border-dashed p-6 text-center transition-colors ${
                isDragging ? "border-gray-700 bg-gray-50" : "border-gray-200"
            }`}
            onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); }}
            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); }}
            onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); }}
            onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsDragging(false);
                if (disabled) return;
                const dropped = e.dataTransfer.files?.[0];
                if (dropped) onPick(dropped);
            }}
        >
            <p className="text-sm font-medium text-gray-900">Glissez-déposez votre CSV ici</p>
            {hint && <p className="text-[11px] text-gray-400 mt-1">{hint}</p>}
            <label className="mt-3 inline-flex cursor-pointer items-center justify-center rounded-xs border border-gray-200 bg-white px-3 py-2 text-xs font-medium text-gray-900 hover:bg-[#F9F6F3]">
                Choisir un fichier
                <input
                    type="file"
                    accept=".csv,text/csv"
                    className="hidden"
                    disabled={disabled}
                    onChange={(e) => onPick(e.target.files?.[0] || null)}
                />
            </label>
            <p className="mt-2 text-xs text-gray-600">{label}</p>
        </div>
    );
}
